/** @flow */

import React, { Component, type Element } from "react";
import Step from "./step";

/*
      The following is not the mars helicopter "Ingenuity"'s source code but it is the LinearWizard code.
      Please be careful with it!

              _..._
            .'     '.      _
           /    .-""-\   _/ \
         .-|   /:.   |  |   |
         |  \  |:.   /.-'-./
         | .-'-;:__.'    =/
         .'=  *=|NASA _.='
        /   _.  |    ;
       ;-.-'|    \   |
      /   | \    _\  _\
      \__/'._;.  ==' ==\
               \    \   |
               /    /   /
               /-._/-._/
               \   `\  \
                `-._/._/

      Helpful notes

      if you wan to manually focus on some step use defaultStep NOT currentStep (actually I'm not sure why there is currentStep at all)

      this.props.handleSubmit:
      It is imperative for every Step component in that wizard to have this.props.handleSubmit(data,isValid)
      Calling that method with some data and isValid = true will make the wizard to go to the next step

      editable:
      this.props.editable becomes available in every Step component, when editable is true the step is on focus

      data:
      this.props.data becomes available in every Step component, use it as u wish

 */

type RawDataType = Array<$Exact<{ data: Object, valid: boolean }> | typeof undefined>;

type OwnProps = $Exact<{
  data?: Array<$Exact<{ data: Object, valid: boolean }>>,
  onChange?: (data: Array<Object>, valid?: boolean, currentStep?: number) => any,
  onSubmit?: (data: Array<Object>, valid?: boolean) => any,
  defaultStep?: number,
  isReadOnly?: boolean,
  children: Array<Element<typeof Step>>,
  step?: number,
  currentStep?: number,
  onToggleEdit?: (index?: number) => any,
  className?: string,
}>;

type State = $Exact<{
  currentStep: number,
  rawData: RawDataType,
  stepsCount: number,
  possibleSteps: Array<number>,
}>;

const arrayReplace = (state: Array<any>, index: number, newItem: Object) => {
  return [...state.slice(0, index), newItem, ...state.slice(index + 1, state.length)];
};

const fillArray = (data: Array<$Exact<{ data: Object, valid: boolean }>> = [], count: number) => {
  let newData: RawDataType = new Array(count);
  data.forEach((item, index) => {
    newData[index] = item;
  });
  return newData;
};

class LinearWizard extends Component<OwnProps, State> {
  state = {
    currentStep: this.props.isReadOnly
      ? 0
      : this.props.defaultStep ||
        this.props.children.findIndex(item => item && !item.props.hidden) + 1,
    rawData: fillArray(this.props.data, this.props.children.length),
    stepsCount: this.props.children.length,
    possibleSteps: this.props.children.reduce((target, item, index) => {
      if (item && !item.props.hidden) {
        target.push(index);
      }
      return target;
    }, []),
  };

  static getDerivedStateFromProps(props: OwnProps, state: State) {
    if (
      props.isReadOnly &&
      props.currentStep != null &&
      props.step != null &&
      props.step > props.currentStep
    ) {
      return {
        currentStep: props.isReadOnly
          ? 0
          : props.defaultStep || props.children.findIndex(item => item && !item.props.hidden) + 1,
        rawData: fillArray(props.data, props.children.length),
        stepsCount: props.children.length,
        possibleSteps: props.children.reduce((target, item, index) => {
          if (item && !item.props.hidden) {
            target.push(index);
          }
          return target;
        }, []),
      };
    }

    return {
      currentStep:
        state.currentStep === 0 && !props.isReadOnly
          ? props.defaultStep || props.children.findIndex(item => item && !item.props.hidden) + 1
          : state.currentStep,
      rawData:
        state.currentStep === 0 && !props.isReadOnly
          ? fillArray(props.data, props.children.length)
          : state.rawData,
      stepsCount: props.children.length,
      possibleSteps: props.children.reduce((target, item, index) => {
        if (item && !item.props.hidden) {
          target.push(index);
        }
        return target;
      }, []),
    };
  }

  getNextStep(step: number): number {
    return this.state.possibleSteps[this.state.possibleSteps.indexOf(step) + 1] + 1;
  }

  onToggleStepEdit = (step: number) => () => {
    this.setState(
      {
        rawData: this.state.rawData.map((el, idx) => {
          return idx <= step ? el : undefined;
        }),
        currentStep: step + 1,
      },
      () => {
        const { onChange, onToggleEdit } = this.props;
        onChange && onChange(this.getRawValues(), this.isValid(), step + 1);
        onToggleEdit && onToggleEdit(step);
      },
    );
  };

  onStepChange = (step: number) => (values: Object = {}, valid: boolean = true) =>
    this.setState(
      {
        rawData: arrayReplace(this.state.rawData, step, {
          data: values,
          valid,
        }),
      },
      () => {
        const { onChange } = this.props;
        onChange && onChange(this.getRawValues(), this.isValid(), step + 1);
      },
    );

  isValid() {
    return this.state.possibleSteps.every(
      step => this.state.rawData[step] != null && this.state.rawData[step].valid,
    );
  }

  getValidValues() {
    // $FlowIgnore It's impossible to add this type because version of Babel is not support this kind of syntax
    return this.state.rawData.filter(part => part && part.valid).map(part => part.data);
  }

  getRawValues() {
    // $FlowIgnore It's impossible to add this type because version of Babel is not support this kind of syntax
    return this.state.rawData.map(part => (part != null ? part.data : {}));
  }

  handleStepSubmit = (step: number) => (
    data: Object,
    valid: boolean,
    disableStepRecount?: boolean,
  ) => {
    this.setState(
      {
        rawData: arrayReplace(this.state.rawData, step, {
          data,
          valid,
        }),
      },
      () => {
        const { onChange } = this.props;
        onChange && onChange(this.getRawValues(), this.isValid(), step + 1);

        // This is a hack to be sure that some child props will not change hidden prop
        setTimeout(() => {
          if (this.isValid()) {
            const { onSubmit } = this.props;
            onSubmit && onSubmit(this.getRawValues(), this.isValid()); // Strange Flow issue without destruction
          }

          const nextStep = disableStepRecount ? this.state.currentStep + 1 : this.getNextStep(step);
          this.setState({
            currentStep: nextStep,
          });
        }, 10);
      },
    );
  };

  getStepState = (step: number) => {
    return this.state.rawData[step] != null ? this.state.rawData[step] : null;
  };

  render() {
    return (
      <div className={this.props.className}>
        {this.props.children.map(
          (element, index) =>
            element &&
            !element.props.hidden &&
            React.cloneElement(element, {
              onChange: this.onStepChange(index),
              editable: this.state.currentStep - 1 === index,
              handleSubmit: this.handleStepSubmit(index),
              isTouched: this.state.rawData[index] !== undefined,
              data: this.state.rawData[index] && this.state.rawData[index].data,
              isValid: this.state.rawData[index] && this.state.rawData[index].valid,
              onToggleEdit: this.onToggleStepEdit(index),
              isCollapsable: false,
              getStepState: this.getStepState,
              step: index,
              currentStep: this.state.currentStep - 1,
              key: index,
            }),
        )}
      </div>
    );
  }
}

export default LinearWizard;
